/**
 * Sign-in blank-card recorder (KAN-916, RD-LUI-010, 2026-10-21).
 *
 * Desktop Safari on the dev site blanks the sign-in card for 100-350ms right
 * after "Signing in...". It never reproduces locally or in automated Safari,
 * so the founder turns this on with `?logindebug=1`, does ONE sign-in, presses
 * Copy in LoginDebugOverlay, and we read what was mounted/visible per frame.
 *
 * Same pattern as keyboardReframeDebug.js (`?kbdebug=1`): DEBUG BUILDS ONLY
 * (IS_DEBUG_BUILD is checked before the URL or storage is read), cached
 * boolean, rolling buffer. `&nomotion=1` (needs logindebug on) makes
 * useReducedMotion report true: the bisect switch.
 *
 * Privacy: never logs tokens, query strings or field values; error messages are
 * capped at 120 chars and every logged string has uuids / emails masked (ldLog).
 * Dev-only file: exempt from i18n (no user-facing strings).
 */

import { IS_DEBUG_BUILD } from '../env/debugBuild';

const KEY = 'cvx:logindebug';
const KEY_NOMOTION = 'cvx:logindebug:nomotion';
const KEY_NOTS = 'cvx:logindebug:noturnstile';
const MAX_ENTRIES = 2500;
const GAP_MS = 40;

let cachedEnabled = null;
let cachedNoMotion = null;
let cachedNoTs = null;
const buffer = [];
const listeners = new Set();
let epoch = 0;
let samplerStarted = false;

function resolveFlags() {
  cachedEnabled = false;
  cachedNoMotion = false;
  cachedNoTs = false;
  if (!IS_DEBUG_BUILD || typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search || '');
    const ld = params.get('logindebug');
    const nm = params.get('nomotion');
    const nt = params.get('noturnstile');
    const ls = window.localStorage;
    if (ld === '0') {
      ls.removeItem(KEY);
      ls.removeItem(KEY_NOMOTION);
      ls.removeItem(KEY_NOTS);
      return;
    }
    if (ld === '1') ls.setItem(KEY, '1');
    cachedEnabled = ls.getItem(KEY) === '1';
    if (!cachedEnabled) return;
    if (nm === '0') ls.removeItem(KEY_NOMOTION);
    else if (nm === '1') ls.setItem(KEY_NOMOTION, '1');
    cachedNoMotion = ls.getItem(KEY_NOMOTION) === '1';
    if (nt === '0') ls.removeItem(KEY_NOTS);
    else if (nt === '1') ls.setItem(KEY_NOTS, '1');
    cachedNoTs = ls.getItem(KEY_NOTS) === '1';
  } catch {
    cachedEnabled = false;
    cachedNoMotion = false;
    cachedNoTs = false;
  }
}

export function loginDebugEnabled() {
  if (cachedEnabled == null) resolveFlags();
  return cachedEnabled;
}

export function loginDebugNoMotion() {
  if (cachedEnabled == null) resolveFlags();
  return cachedEnabled && cachedNoMotion;
}

/** Dev bisect: `&noturnstile=1` -> the gate renders no widget at all. */
export function loginDebugNoTurnstile() {
  if (cachedEnabled == null) resolveFlags();
  return cachedEnabled && cachedNoTs;
}

const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

function notify() {
  listeners.forEach((fn) => {
    try { fn(buffer.length); } catch { /* listener errors never break logging */ }
  });
}

// Security review (2026-10-21): a logged string can carry an id or an address
// by accident (a factor id as a URL's last segment, a row test id with a record
// uuid, an error message echoing an email). Mask them at the ONE entry point so
// no hook has to remember to.
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const EMAIL_RE = /[^\s@/:"']+@[^\s@/:"']+\.[a-z]{2,}/gi;
const scrub = (v) => (typeof v === 'string' ? v.replace(UUID_RE, ':id').replace(EMAIL_RE, ':email') : v);
function scrubData(data) {
  if (!data || typeof data !== 'object') return data;
  const out = {};
  Object.keys(data).forEach((k) => { out[k] = scrub(data[k]); });
  return out;
}

export function ldLog(type, data) {
  if (!loginDebugEnabled()) return;
  const now = nowMs();
  if (!epoch) epoch = now;
  buffer.push({ t: Math.round((now - epoch) * 10) / 10, type, data: scrubData(data) });
  if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES);
  notify();
}

export function ldMark(label) {
  ldLog('mark', { label });
}

export function ldSubscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function ldEntryCount() {
  return buffer.length;
}

export function ldClear() {
  buffer.length = 0;
  notify();
}

const fmtVal = (v) => (typeof v === 'number' ? String(Math.round(v * 100) / 100) : String(v));

export function ldFormatLog() {
  const meta = typeof window !== 'undefined'
    ? `# logindebug ${window.navigator.userAgent}\n# nomotion=${loginDebugNoMotion()} noturnstile=${loginDebugNoTurnstile()} innerW=${window.innerWidth} innerH=${window.innerHeight}\n`
    : '';
  return meta + buffer
    .map((e) => `+${String(e.t).padStart(8)} ${e.type}${
      e.data ? ` ${Object.entries(e.data).map(([k, v]) => `${k}=${fmtVal(v)}`).join(' ')}` : ''
    }`)
    .join('\n');
}

// ---------------------------------------------------------------- sampler

const q = (sel) => document.querySelector(sel);
const cs = (el) => window.getComputedStyle(el);
const op = (el) => Math.round(parseFloat(cs(el).opacity) * 100) / 100;
const short = (s, n = 120) => String(s == null ? '' : s).slice(0, n);

function nearestTestId(el) {
  let node = el;
  while (node && node.nodeType === 1) {
    const id = node.getAttribute && node.getAttribute('data-testid');
    if (id) return id;
    node = node.parentElement;
  }
  return el && el.tagName ? el.tagName : 'none';
}

// Heisenberg note: five getComputedStyle reads + elementFromPoint force style
// and layout EVERY frame while the flag is on. Fine for a diagnostic, but the
// timings are "about", never to the ms -- the probe can perturb the tween it hunts.
function sample() {
  const s = {
    vis: (document.visibilityState || '?')[0],
    foc: document.hasFocus() ? 1 : 0,
  };
  const card = q('[data-testid="auth-card"]');
  let cx = window.innerWidth / 2;
  let cy = window.innerHeight / 2;
  s.card = card ? 1 : 0;
  if (card) {
    const r = card.getBoundingClientRect();
    const c = cs(card);
    s.ch = Math.round(r.height);
    s.co = Math.round(parseFloat(c.opacity) * 100) / 100;
    s.cv = (c.visibility || '?')[0];
    cx = r.left + r.width / 2;
    cy = r.top + r.height / 2;
  }
  const wrap = q('[data-testid="auth-step-wrap"]');
  if (wrap) {
    s.wh = Math.round(wrap.getBoundingClientRect().height);
    s.wo = (cs(wrap).overflow || '?')[0];
    const layer = wrap.firstElementChild;
    if (layer) s.eo = op(layer);
  }
  const ghost = q('[data-testid="auth-step-leaving"]');
  s.gh = ghost ? 1 : 0;
  if (ghost) s.go = op(ghost);
  const splash = q('[data-testid="splash-overlay"]');
  if (splash) {
    s.sv = splash.getAttribute('data-visible');
    s.so = op(splash);
  }
  s.jr = q('[data-cvx-logo-journey]') ? 1 : 0;
  const box = q('[data-turnstile-container]');
  const shadow = box && box.shadowRoot;
  const frames = Array.from(document.querySelectorAll('iframe'));
  if (shadow) frames.push(...Array.from(shadow.querySelectorAll('iframe')));
  s.ts = frames.length;
  s.tsh = shadow ? 1 : 0;
  const ts = frames.find((f) => (box && box.contains(f)) || /cloudflare/.test(f.getAttribute('src') || '')) || null;
  if (ts) {
    const r = ts.getBoundingClientRect();
    s.tw = Math.round(r.width);
    s.th = Math.round(r.height);
    s.tv = (cs(ts).visibility || '?')[0];
  }
  const hit = document.elementFromPoint(cx, cy);
  s.hit = hit ? nearestTestId(hit) : 'none';
  const a = document.activeElement;
  s.act = a ? `${a.tagName}${a.getAttribute && a.getAttribute('type') ? `:${a.getAttribute('type')}` : ''}` : 'none';
  const root = q('#root');
  s.root = root ? root.childElementCount : -1;
  return s;
}

const sig = (s) => JSON.stringify(s);

function describe(target) {
  if (!target || !target.tagName) return 'none';
  const id = target.getAttribute && (target.getAttribute('data-testid') || target.getAttribute('name') || target.getAttribute('type'));
  return id ? `${target.tagName}#${id}` : target.tagName;
}

const lastSegment = (url) => {
  try {
    const seg = new URL(url, window.location.href).pathname.split('/').filter(Boolean).pop();
    return seg || '/';
  } catch {
    return '?';
  }
};

const hostOf = (src) => {
  try { return new URL(src, window.location.href).hostname || 'none'; } catch { return '?'; }
};

function observeIframes(root, scope) {
  try {
    const rep = (ev) => (n) => {
      if (!n || n.tagName !== 'IFRAME') return;
      const r = n.getBoundingClientRect();
      ldLog(ev, { scope, host: hostOf(n.getAttribute('src') || ''), w: Math.round(r.width), h: Math.round(r.height) });
    };
    new MutationObserver((muts) => muts.forEach((m) => {
      m.addedNodes.forEach(rep('iframe.add'));
      m.removedNodes.forEach(rep('iframe.remove'));
    })).observe(root, { childList: true, subtree: true });
  } catch { /* noop */ }
}

/** Called by HCaptchaGate after render(): watch the container's open shadowRoot. No-op when off. */
export function ldObserveShadow(el) {
  if (!loginDebugEnabled() || !el || !el.shadowRoot) return;
  observeIframes(el.shadowRoot, 'shadow');
}

function installListeners() {
  observeIframes(document.documentElement, 'doc');
  const on = (target, ev, fn, opts) => { try { target.addEventListener(ev, fn, opts); } catch { /* noop */ } };
  on(document, 'visibilitychange', () => ldLog('visibilitychange', { vis: document.visibilityState }));
  on(window, 'pagehide', () => ldLog('pagehide'));
  on(window, 'pageshow', () => ldLog('pageshow'));
  on(window, 'beforeunload', () => ldLog('beforeunload'));
  on(document, 'focusin', (e) => ldLog('focusin', { target: describe(e.target) }), true);
  on(document, 'focusout', (e) => ldLog('focusout', { target: describe(e.target) }), true);
  if (document.fonts) {
    on(document.fonts, 'loadingdone', () => ldLog('fonts.loadingdone'));
    on(document.fonts, 'loadingerror', () => ldLog('fonts.loadingerror'));
  }
  on(window, 'error', (e) => ldLog('error', { msg: short(e && e.message) }));
  on(window, 'unhandledrejection', (e) => ldLog('unhandledrejection', {
    msg: short(e && e.reason && e.reason.message ? e.reason.message : e && e.reason),
  }));
  try {
    const po = new PerformanceObserver((list) => {
      list.getEntries().forEach((en) => {
        const own = en.name.startsWith(window.location.origin) || /supabase\.(co|in)/.test(en.name);
        if (!own) return;
        ldLog('resource', { name: lastSegment(en.name), kind: en.initiatorType, dur: en.duration });
      });
    });
    po.observe({ type: 'resource', buffered: false });
  } catch { /* unsupported entry type */ }
  try {
    new MutationObserver((muts) => {
      muts.forEach((m) => m.addedNodes.forEach((n) => {
        if (n.tagName !== 'LINK' && n.tagName !== 'SCRIPT') return;
        const url = n.getAttribute('href') || n.getAttribute('src');
        if (url) ldLog('head.add', { tag: n.tagName, name: lastSegment(url) });
      }));
    }).observe(document.head, { childList: true });
  } catch { /* noop */ }
}

/** Page-lifetime: the rAF loop and the listeners never stop (root-mounted overlay). */
export function startLoginDebugSampler() {
  if (samplerStarted || !loginDebugEnabled()) return;
  if (typeof window === 'undefined' || typeof requestAnimationFrame !== 'function') return;
  samplerStarted = true;
  installListeners();
  let prevSig = '';
  let prevT = 0;
  const frame = (t) => {
    if (prevT && t - prevT > GAP_MS) ldLog('frame.gap', { gap: Math.round(t - prevT) });
    prevT = t;
    try {
      const s = sample();
      const next = sig(s);
      if (next !== prevSig) {
        prevSig = next;
        ldLog('s', s);
      }
    } catch { /* a probe failure must never stop the loop */ }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

/** @internal test helper */
export function __resetLoginDebugForTests() {
  cachedEnabled = null;
  cachedNoMotion = null;
  cachedNoTs = null;
  buffer.length = 0;
  listeners.clear();
  epoch = 0;
  samplerStarted = false;
}
